import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { journalApi } from '../../api/journal';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { useState } from 'react';
import type { JournalEntryDto } from '../../types/journal';

export default function JournalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);

  const cloneMutation = useMutation({
    mutationFn: () => journalApi.clone(Number(id), {
      newDate: new Date().toISOString().slice(0, 10),
    }),
    onSuccess: (newId) => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      navigate(`/journal/${newId}`);
    },
  });

  const { data: entry, isLoading } = useQuery<JournalEntryDto>({
    queryKey: ['journal-entry', id],
    queryFn: () => journalApi.getById(Number(id)),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => journalApi.delete(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      addToast(`Journal entry #${entry?.documentNumber || id} deleted`, 'success');
      navigate('/journal');
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (!entry) return <div className="text-center py-8 text-gray-500">Entry not found</div>;

  const balanced = Math.abs(entry.totalDebit - entry.totalCredit) < 0.005;

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Journal Entry #{entry.documentNumber}
          </h1>
          <p className="text-sm text-gray-500">ID: {entry.id} | Type: {entry.documentType}</p>
        </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
            onClick={() => navigate(`/journal/${entry.id}/edit`)}
          >
            Edit
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
            onClick={() => setShowCloneDialog(true)}
          >
            Clone
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>

      {/* Entry details */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500 text-xs">Date</div>
            <div className="font-medium">{formatDate(entry.date)}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Document #</div>
            <div className="font-medium">{entry.documentNumber}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Document Type</div>
            <div className="font-medium">{entry.documentType}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Person</div>
            <div className="font-medium">{entry.personName ?? '-'}</div>
          </div>
        </div>
        <div className="mt-3">
          <div className="text-gray-500 text-xs">Content</div>
          <div className="text-sm">{entry.content}</div>
        </div>
        <div className="mt-3 flex gap-4">
          <div className={`text-sm font-medium ${balanced ? 'text-green-600' : 'text-red-600'}`}>
            {balanced ? 'Balanced' : 'UNBALANCED'}
          </div>
          <div className="text-sm">
            Debit: <span className="num font-semibold">{formatMoney(entry.totalDebit)}</span>
          </div>
          <div className="text-sm">
            Credit: <span className="num font-semibold">{formatMoney(entry.totalCredit)}</span>
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-2 border-b border-gray-200">
          <h2 className="font-semibold text-sm">Lines</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-right">Account</th>
                <th className="text-left">Account Name</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
                <th className="text-left">Person</th>
              </tr>
            </thead>
            <tbody>
              {entry.lines.map((line) => (
                <tr key={line.id}>
                  <td className="num">{line.account}</td>
                  <td>{line.accountName}</td>
                  <td className="num">
                    {line.entryType === 'Debetas' ? formatMoney(line.amount) : ''}
                  </td>
                  <td className="num">
                    {line.entryType === 'Kreditas' ? formatMoney(line.amount) : ''}
                  </td>
                  <td>{line.personId ? `Person ${line.personId}` : '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} className="text-right font-semibold">Total</td>
                <td className="num font-semibold">{formatMoney(entry.totalDebit)}</td>
                <td className="num font-semibold">{formatMoney(entry.totalCredit)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Delete dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        title="Delete Journal Entry"
        message={`Permanently delete journal entry #${entry.documentNumber}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Clone dialog */}
      <ConfirmDialog
        open={showCloneDialog}
        title="Clone Journal Entry"
        message={`Create a copy of journal entry #${entry.documentNumber} with today's date?`}
        confirmLabel="Clone"
        onConfirm={() => cloneMutation.mutate()}
        onCancel={() => setShowCloneDialog(false)}
      />
    </div>
  );
}
